import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Chip, CircularProgress, Divider, InputBase,
    List, ListItemButton, ListItemIcon, ListItemText, Pagination,
    Tab, Tabs, Typography,
} from '@mui/material';
import {
    AccountBalance as AccountIcon,
    Assignment as TaskIcon,
    Business as ClientIcon,
    Folder as ProjectIcon,
    Group as GroupIcon,
    Person as UserIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { search } from '../redux/ducks/search';
import { alpha } from '@mui/system';
import styled from '@emotion/styled';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import ActiveChip from '../components/ActiveChip';

// ─── Styled Components ────────────────────────────────────────────────────────

const PageContainer = styled(Box)(({ theme }) => ({
    maxWidth: theme.breakpoints.values.xl,
    margin: '0 auto',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
}));

const SearchBar = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
}));

const SearchInput = styled(InputBase)(({ theme }) => ({
    fontSize: theme.typography.size.base,
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
}));

const TabLabelBox = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
});

const TabCountChip = styled(Chip)(({ theme }) => ({
    height: theme.spacing(2),
    fontSize: theme.typography.size.xs,
}));

const LoadingBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
    textAlign: 'center',
}));

const TypeSection = styled(Box, {
    shouldForwardProp: prop => prop !== '$hasMargin',
})(({ theme, $hasMargin }) => ({
    marginBottom: $hasMargin ? theme.spacing(3) : 0,
}));

const TypeHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
}));

const ResultList = styled(List)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const ResultListItem = styled(ListItemButton, {
    shouldForwardProp: prop => prop !== '$isDeleted',
})(({ theme, $isDeleted }) => ({
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
    textDecoration: $isDeleted ? 'line-through' : 'none',
    opacity: $isDeleted ? 0.6 : 1,
}));

const ResultItemIcon = styled(ListItemIcon)({
    minWidth: 36,
});

const PrimaryBox = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
});

const ItemName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
}));

const PaginationBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const ALL_TYPES = ['clients', 'accounts', 'projects', 'tasks', 'groups', 'users'];

const STATUS_DOMAIN = { accounts: 'account', projects: 'project', tasks: 'task' };

const TYPE_CONFIG = {
    clients:  { label: 'Clients',  Icon: ClientIcon  },
    accounts: { label: 'Accounts', Icon: AccountIcon },
    projects: { label: 'Projects', Icon: ProjectIcon },
    tasks:    { label: 'Tasks',    Icon: TaskIcon    },
    groups:   { label: 'Groups',   Icon: GroupIcon   },
    users:    { label: 'Users',    Icon: UserIcon    },
};

const getItemLabel = (type, item) => {
    if (type === 'users') return `${item.first_name} ${item.last_name}`;
    return item.name || '';
};

const getItemSecondary = (type, item) => {
    switch (type) {
        case 'clients':  return item.slug || '';
        case 'accounts': return item.client || '';
        case 'projects': return '';
        case 'tasks':    return '';
        case 'groups':   return item.account || '';
        case 'users':    return [item.clientName, item.role].filter(Boolean).join(' • ');
        default: return '';
    }
};

const getItemUrl = (type, item) => {
    switch (type) {
        case 'clients':  return `/client/${item.slug}`;
        case 'accounts': return `/account/${item.slug}`;
        case 'projects': return item.account ? `/account/${item.account}/project/${item._id}` : '#';
        case 'tasks':    return item.account && item.project
            ? `/account/${item.account}/project/${item.project}`
            : '#';
        case 'groups':   return item.account ? `/account/${item.account}` : '#';
        case 'users':    return `/user/${item._id}`;
        default: return '#';
    }
};

const EMPTY_RESULTS = ALL_TYPES.reduce((acc, t) => ({ ...acc, [t]: [], totals: {} }), { totals: {} });

// ─── Component ────────────────────────────────────────────────────────────────

const Search = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const initialQuery = searchParams.get('q') || '';
    const initialType  = searchParams.get('type') || 'all';

    const [inputValue, setInputValue] = useState(initialQuery);
    const [query, setQuery]           = useState(initialQuery);
    const [activeTab, setActiveTab]   = useState(initialType);
    const [results, setResults]       = useState(EMPTY_RESULTS);
    const [loading, setLoading]       = useState(false);
    const [page, setPage]             = useState(1);
    const debounceRef = useRef(null);

    const runSearch = useCallback((q, type, pageNum = 1) => {
        if (!q || q.trim().length < 2) {
            setResults(EMPTY_RESULTS);
            return;
        }
        setLoading(true);
        dispatch(search(q.trim(), type, (data) => {
            setResults(data);
            setLoading(false);
        }, { from: (pageNum - 1) * PAGE_SIZE, limit: PAGE_SIZE }));
        setSearchParams({ q: q.trim(), type }, { replace: true });
    }, [dispatch, setSearchParams]);

    useEffect(() => {
        if (query.trim().length >= 2) {
            runSearch(query, activeTab, page);
        }
    }, [query, activeTab, page]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleInputChange = (e) => {
        const val = e.target.value;
        setInputValue(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setQuery(val);
            setPage(1);
        }, 300);
    };

    const handleTabChange = (_, newTab) => {
        setActiveTab(newTab);
        setPage(1);
    };

    const handlePageChange = (_, newPage) => {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleNavigate = (type, item) => {
        navigate(getItemUrl(type, item));
    };

    const visibleTypes = activeTab === 'all' ? ALL_TYPES : [activeTab];
    const totalResults = ALL_TYPES.reduce((sum, t) => sum + (results?.totals?.[t] || 0), 0);
    const currentTotal = activeTab === 'all' ? totalResults : (results?.totals?.[activeTab] || 0);
    const pageCount = Math.ceil(currentTotal / PAGE_SIZE);

    return (
        <PageContainer>
            <PageHeader title="Search" subtitle={query ? `Results for "${query}"` : undefined} />
            <SearchBar>
                <SearchIcon color="action" />
                <SearchInput
                    fullWidth
                    value={inputValue}
                    onChange={handleInputChange}
                    placeholder="Search clients, accounts, projects, users..."
                    inputProps={{ 'aria-label': 'search' }}
                />
                {loading && <CircularProgress size={20} />}
            </SearchBar>

            <StyledTabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
            >
                <Tab
                    label={
                        <TabLabelBox>
                            All
                            {totalResults > 0 && (
                                <TabCountChip size="small" label={totalResults} />
                            )}
                        </TabLabelBox>
                    }
                    value="all"
                />
                {ALL_TYPES.map(type => {
                    const count = results?.totals?.[type] || 0;
                    return (
                        <Tab
                            key={type}
                            value={type}
                            label={
                                <TabLabelBox>
                                    {TYPE_CONFIG[type].label}
                                    {count > 0 && (
                                        <TabCountChip size="small" label={count} />
                                    )}
                                </TabLabelBox>
                            }
                        />
                    );
                })}
            </StyledTabs>

            {loading ? (
                <LoadingBox>
                    <CircularProgress />
                </LoadingBox>
            ) : query.trim().length < 2 ? (
                <EmptyStateBox>
                    <Typography color="text.secondary">Type at least 2 characters to search</Typography>
                </EmptyStateBox>
            ) : (
                <>
                    {visibleTypes.map(type => {
                        const items = results[type] || [];
                        if (items.length === 0) return null;
                        const { Icon, label } = TYPE_CONFIG[type];
                        return (
                            <TypeSection key={type} $hasMargin={activeTab === 'all'}>
                                {activeTab === 'all' && (
                                    <TypeHeader>
                                        <Icon fontSize="small" color="action" />
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {label}
                                        </Typography>
                                        <Typography variant="caption" color="text.disabled">
                                            ({results?.totals?.[type] || 0} total)
                                        </Typography>
                                    </TypeHeader>
                                )}
                                <ResultList dense disablePadding>
                                    {items.map((item, idx) => (
                                        <React.Fragment key={`${type}-${item._id}`}>
                                            <ResultListItem
                                                onClick={() => handleNavigate(type, item)}
                                                $isDeleted={item.isDeleted}
                                            >
                                                <ResultItemIcon>
                                                    <Icon fontSize="small" color={item.isDeleted ? 'disabled' : 'action'} />
                                                </ResultItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <PrimaryBox>
                                                            <ItemName variant="body2">
                                                                {getItemLabel(type, item)}
                                                            </ItemName>
                                                            {item.isDeleted && (
                                                                <Chip size="small" variant="outlined" color="error" label="Deleted" />
                                                            )}
                                                            {STATUS_DOMAIN[type] && item.status && (
                                                                <StatusBadge domain={STATUS_DOMAIN[type]} status={item.status} disabled={item.isDeleted} />
                                                            )}
                                                            {(type === 'clients' || type === 'users') && (
                                                                <ActiveChip status={item.active ? 'Active' : 'Inactive'} disabled={item.isDeleted} />
                                                            )}
                                                        </PrimaryBox>
                                                    }
                                                    secondary={getItemSecondary(type, item)}
                                                    primaryTypographyProps={{ component: 'div' }}
                                                />
                                            </ResultListItem>
                                            {idx < items.length - 1 && <Divider component="li" />}
                                        </React.Fragment>
                                    ))}
                                </ResultList>
                            </TypeSection>
                        );
                    })}

                    {visibleTypes.every(t => (results[t] || []).length === 0) && (
                        <EmptyStateBox>
                            <Typography color="text.secondary">No results for "{query}"</Typography>
                        </EmptyStateBox>
                    )}

                    {activeTab !== 'all' && pageCount > 1 && (
                        <PaginationBox>
                            <Pagination
                                count={pageCount}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                            />
                        </PaginationBox>
                    )}
                </>
            )}
        </PageContainer>
    );
};

export default Search;
